import "./ClawController.scss";
import React, { useCallback, useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "../../api/axios";
import { filesRoute, getCampaignCustomisationVarsRoute, getCampaignFeatureVarsRoute, getCampaignOverviewRoute, getFeatureVarsRoute, getStreamCustomisationVarsRoute, streamConnectionStatusRoute } from "../../api/routes";
import { DATA_SAVE_LEVELS, deepObjectMerge, POST_MESSAGE_IN_TYPES, POST_MESSAGE_OUT_TYPES, resetOnLoadDefaultFeatureVars } from "../../helpers/FeatureControlsHelper";
import { assets } from "../../assets/assets";
import { CampaignContext } from "../../contexts/CampaignContext";
import { CUSTOMISED_ASSETS_BASE_URL, CUSTOMISED_URL_MAP, getValFromObject } from "../../config/FeatureDashboardVarMap";
import { getFeatureAssetBaseUrl, resolveCustomAssetUrl } from "../../helpers/FeatureAssets";
import { getCustomFontUrl } from "../../helpers/CustomFonts";
import { findFeature, findFeatureByVarKey } from "../../helpers/CampaignsHelper";
import config from "../../config";
import { PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS } from "../../helpers/Colours";
import { blankImageData } from "../TimelineEditor/slideout-panel/image-uploader/ImageUploaderContent";
import { sanitizeCustomisationData } from "../../helpers/CustomisationDataProcessors";
import { fixSrcUrls } from "../../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import BasicMachineCameraFeed from "../../components/FeatureControls/custom-control-components/claw-camera-feeds/BasicMachineCameraFeed";
import notAvailableImage from "./assets/NotAvailable.png";
import selectorImage from "./assets/Selector.png";
import grid10x10Image from "./assets/grid_10x10_full.png";
import grid15x15Image from "./assets/grid_15x15_full.png";
import grid20x20Image from "./assets/grid_20x20_full.png";
import { CONFETTI_EFFECT_CONTROLLER } from "../../effects/confetti/ConfettiEffectController";

const CLAW_PHASE_IDS = {
    FEATURE_STARTED: 'featureStarted',
    FEATURE_ENDED: 'featureEnded',
    CREDIT_START: 'creditStart',
    OPT_IN: 'optInToPlay',
    POSITION_VOTING_STARTED: 'positionVotingStarted',
    POSITION_VOTING_ENDED: 'positionVotingEnded',

    // Voting is active during POSITION_SELECT phase
    POSITION_SELECT: 'positionSelect',

    PLAY_MOVE: 'playMove',
    WIN_PRIZE: 'winPrize',
    LOSE: 'lose',
    WINNER_SELECTED: 'winnerSelected',
}
const IDLE_PHASES = [
    CLAW_PHASE_IDS.FEATURE_STARTED,
    CLAW_PHASE_IDS.FEATURE_ENDED,
    CLAW_PHASE_IDS.CREDIT_START,
    CLAW_PHASE_IDS.OPT_IN,
    CLAW_PHASE_IDS.POSITION_VOTING_STARTED,
]
const SHOW_CAM_PHASES = [
    CLAW_PHASE_IDS.POSITION_VOTING_ENDED,
    CLAW_PHASE_IDS.PLAY_MOVE,
    CLAW_PHASE_IDS.WIN_PRIZE,
    CLAW_PHASE_IDS.LOSE,
    CLAW_PHASE_IDS.WINNER_SELECTED
]

const GRID_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const COORDINATE_CONVERTOR = {
    GRID_FRONT_LEFT_ALL: {"20x20": -542, "15x15": -548, "10x10": -550},
    GRID_FRONT_RIGHT_ALL: {"20x20": 540, "15x15": 550, "10x10": 590},
    GRID_FRONT_ALL: {"20x20": 1117, "15x15": 1130, "10x10": 1162},
    GRID_BACK_LEFT_ALL: {"20x20": -403, "15x15": -402, "10x10": -400},
    GRID_BACK_RIGHT_ALL: {"20x20": 390, "15x15": 398, "10x10": 420},
    GRID_BACK_ALL: {"20x20": 560, "15x15": 565, "10x10": 575},

    applyPerspective: function (gridX_in, gridY_in, gridSize = 20) {
        const GRID_FRONT_LEFT = this.GRID_FRONT_LEFT_ALL[gridSize + 'x' + gridSize];
        const GRID_FRONT_RIGHT = this.GRID_FRONT_RIGHT_ALL[gridSize + 'x' + gridSize];
        const GRID_FRONT = this.GRID_FRONT_ALL[gridSize + 'x' + gridSize];
        const GRID_BACK_LEFT = this.GRID_BACK_LEFT_ALL[gridSize + 'x' + gridSize];
        const GRID_BACK_RIGHT = this.GRID_BACK_RIGHT_ALL[gridSize + 'x' + gridSize];
        const GRID_BACK = this.GRID_BACK_ALL[gridSize + 'x' + gridSize];

        const gridX = gridX_in;
        const gridY = gridY_in;
        const yStep = (GRID_FRONT - GRID_BACK) / (gridSize + 1);
        const y = (GRID_FRONT - GRID_BACK) + (gridY * yStep) + yStep / 2;
        const yPercent = 1 - ((y - GRID_BACK) / (GRID_FRONT - GRID_BACK));
        const gridPosXOffset = (GRID_BACK_LEFT - GRID_FRONT_LEFT) * yPercent;
        const gridRightXOffset = (GRID_FRONT_RIGHT - GRID_BACK_RIGHT) * yPercent;
        const xStep = (GRID_FRONT_RIGHT - GRID_FRONT_LEFT - (gridPosXOffset + gridRightXOffset)) / (gridSize + 1);
        const x = GRID_FRONT_LEFT + gridPosXOffset + (gridX * xStep) + xStep / 2;

        // Small tunable translation (in native/full-size-image px) to nudge every point up/right -
        // adjust these two if markers are still consistently off in one direction.
        const TRANSLATE_X = 27;
        const TRANSLATE_Y = 20;
        return {x: x + TRANSLATE_X, y: (GRID_FRONT - y) + TRANSLATE_Y};
    }
}

// Block of unusable cells sits in the bottom-left of the grid.
const UNUSABLE_GRID_COUNTS = {
    "_10x10": 4,
    "_15x15": 6,
    "_20x20": 8,
}
const isPositionUnusable = (gridSize, colIndex, rowIndex) => {
    const count = UNUSABLE_GRID_COUNTS[`_${gridSize}x${gridSize}`] ?? 0;
    return count > 0 && colIndex < count && rowIndex >= gridSize - count;
}

// Grid outline overlaid on the camera feed in "Live Grid" view, one per selectable grid size.
const GRID_OVERLAY_IMAGES = {
    10: grid10x10Image,
    15: grid15x15Image,
    20: grid20x20Image,
}

function ClawController() {
    const [initialising, setInitialising] = useState(true);
    const [clawFound, setClawFound] = useState(false);
    const [clawFeatureId, setClawFeatureId] = useState(null);
    const clawFeatureId_ref = useRef(null);
    useEffect(() => {
        clawFeatureId_ref.current = clawFeatureId;
    }, [clawFeatureId]);
    const navigate = useNavigate();
    const [clawCustomisationData, setClawCustomisationData] = useState(null);
    const clawCustomisationData_ref = useRef(null);
    useEffect(() => {
        clawCustomisationData_ref.current = clawCustomisationData;
    }, [clawCustomisationData]);
    const [clawDashboardVars, setClawDashboardVars] = useState(null);
    const clawDashboardVars_ref = useRef(null);
    useEffect(() => {
        clawDashboardVars_ref.current = clawDashboardVars;
    }, [clawDashboardVars]);
    const cameraYOffsetSetting = Number(clawDashboardVars?.featureControl?.cameraYOffset);
    const cameraYOffsetLevel = Number.isFinite(cameraYOffsetSetting) ? cameraYOffsetSetting : 5;
    const gridYOffsetSetting = Number(clawDashboardVars?.featureControl?.gridYOffset);
    const gridYOffsetLevel = Number.isFinite(gridYOffsetSetting) ? gridYOffsetSetting : 5;
    const [mainFeaturePageLoaded, setMainFeaturePageLoaded] = useState(false);

    const [clawPhase, setClawPhase] = useState(null);
    // Turns are 1-indexed (multiStreamerSetup keys are streamer1, streamer2, ...); default to 1 since the
    // feature may never explicitly report currentStreamersTurn for the very first turn.
    const [streamersTurn, setStreamersTurn] = useState(1);
    const streamersTurn_ref = useRef(1);
    useEffect(() => {
        streamersTurn_ref.current = streamersTurn;
    }, [streamersTurn]);
    // Which selection mode (slider vs poll) is in play; locked in once per credit rather than recomputed every render.
    const [gameMode, setGameMode] = useState(null);
    const [rawClawPhaseData, setRawClawPhaseData] = useState(null);
    // const [positionSelectMode, setPositionSelectMode] = useState(null);
    const [yourSelectedPosition, setYourSelectedPosition] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [liveVotes, setLiveVotes] = useState([]);
    const [gridSize, setGridSize] = useState(10);
    const [pollOptions, setPollOptions] = useState([]);
    const [lastResultWin, setLastResultWin] = useState(null);
    const [lastResultSelectedPlayer, setLastResultSelectedPlayer] = useState(null);
    const [lastResultSelectedPlayerChannel, setLastResultSelectedPlayerChannel] = useState(null);
    const [idleCamView, setIdleCamView] = useState('front');
    const [optionSelectView, setOptionSelectView] = useState('grid');

    // Name entered via the on-load modal; nameInputValue tracks the live field, controllerName the confirmed value.
    const [controllerName, setControllerName] = useState("Claw Controller");
    const [showNameModal, setShowNameModal] = useState(true);
    const [nameInputValue, setNameInputValue] = useState("Claw Controller");

    // Android/Chrome only - any tap while not fullscreen requests it (must run inside the user gesture).
    const viewportRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    useEffect(() => {
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);
    // click (not pointerdown) is used because mobile Chrome only grants fullscreen's required user
    // activation once the touch sequence completes, so requesting on pointerdown/touchstart fails.
    const handleViewportClickCapture = useCallback(() => {
        if (!document.fullscreenElement && viewportRef.current?.requestFullscreen) {
            viewportRef.current.requestFullscreen().catch(() => {});
        }
    }, []);

    // Currently selected grid square: 0-based col (from the left / letters) and row (from the top).
    // Null = no selection yet, in which case the sliders sit in the middle of the grid.
    const [selectedCell, setSelectedCell] = useState(null);
    const selectedCell_ref = useRef(null);
    const clawGridRef = useRef(null);
    const sliderDragAxis_ref = useRef(null);
    const gridSize_ref = useRef(gridSize);
    useEffect(() => {
        gridSize_ref.current = gridSize;
    }, [gridSize]);

    // Tracks the rendered size of the camera feed, so the (full-size-image-based) perspective
    // coordinates from COORDINATE_CONVERTOR can be scaled down to match the displayed grid overlay.
    // A callback ref is used (rather than useRef + a mount-only effect) because the camera feed div
    // only exists while clawPhase is POSITION_SELECT, so it can mount/unmount after this component does.
    const [cameraFeedEl, setCameraFeedEl] = useState(null);
    const cameraFeedRef = useCallback((node) => {
        setCameraFeedEl(node);
    }, []);
    const [cameraFeedSize, setCameraFeedSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        if (!cameraFeedEl) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            if (entry) {
                setCameraFeedSize({ width: entry.contentRect.width, height: entry.contentRect.height });
            }
        });
        observer.observe(cameraFeedEl);
        return () => observer.disconnect();
    }, [cameraFeedEl]);
    const liveGridYOffset = (-22 * (Math.max(1, Math.min(5, gridYOffsetLevel)) - 1) - 22) * (cameraFeedSize.width / 600);

    // Native pixel size of the current grid overlay image, needed to scale perspective coordinates
    // (which are calibrated against the full-size image) down to its "contain"-fitted display size.
    // Measured from a hidden reference <img> (rather than derived from the container's own aspect
    // ratio), since it's sized to its actual rendered box - i.e. the grid image's own scale.
    const [gridImageRefEl, setGridImageRefEl] = useState(null);
    const gridImageRefCallback = useCallback((node) => {
        setGridImageRefEl(node);
    }, []);
    const [gridImageBox, setGridImageBox] = useState(null);
    useEffect(() => {
        if (!gridImageRefEl || !cameraFeedEl) {
            return;
        }
        const measure = () => {
            if (!gridImageRefEl.naturalWidth || !gridImageRefEl.naturalHeight) {
                return;
            }
            // offsetLeft/offsetTop ignore the CSS transform used to horizontally centre the reference
            // image, so getBoundingClientRect (which is transform-aware) is used instead.
            const containerRect = cameraFeedEl.getBoundingClientRect();
            const imgRect = gridImageRefEl.getBoundingClientRect();
            setGridImageBox({
                width: imgRect.width,
                height: imgRect.height,
                left: imgRect.left - containerRect.left,
                top: imgRect.top - containerRect.top - liveGridYOffset,
                naturalWidth: gridImageRefEl.naturalWidth,
                naturalHeight: gridImageRefEl.naturalHeight,
            });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(gridImageRefEl);
        return () => observer.disconnect();
    }, [gridImageRefEl, cameraFeedEl, gridSize, liveGridYOffset]);

    // Selects a grid square.
    const updateSelectedCell = useCallback((colIndex, rowIndex) => {
        const gs = gridSize_ref.current;
        const col = Math.max(0, Math.min(gs - 1, colIndex));
        const row = Math.max(0, Math.min(gs - 1, rowIndex));
        if (isPositionUnusable(gs, col, row)) {
            // Disallowed area: leave the highlight, selection, and marker exactly where they were.
            return;
        }
        selectedCell_ref.current = { col, row };
        setSelectedCell({ col, row });
    }, []);
    const resetSelectedCell = useCallback(() => {
        selectedCell_ref.current = null;
        setSelectedCell(null);
    }, []);

    // Window-level pointer handlers (pointer events cover both mouse and touch). They're attached on
    // drag start rather than using pointer capture on the handle, because the grid re-mounts on state
    // changes which would otherwise break the drag mid-move.
    const handleGridSliderPointerMove = useCallback((e) => {
        const axis = sliderDragAxis_ref.current;
        if (!axis || !clawGridRef.current) {
            return;
        }
        const gs = gridSize_ref.current;
        const rect = clawGridRef.current.getBoundingClientRect();
        const midIndex = Math.floor(gs / 2);
        const current = selectedCell_ref.current ?? { col: midIndex, row: midIndex };
        if (axis === 'row') {
            // Grid has an extra label row at the bottom, so cell height is rect.height / (gs + 1).
            const cellHeight = rect.height / (gs + 1);
            if (cellHeight <= 0) {
                return;
            }
            const row = Math.max(0, Math.min(gs - 1, Math.floor((e.clientY - rect.top) / cellHeight)));
            if (row !== current.row) {
                updateSelectedCell(current.col, row);
            }
        } else {
            // Grid has an extra label column on the right, so cell width is rect.width / (gs + 1).
            const cellWidth = rect.width / (gs + 1);
            if (cellWidth <= 0) {
                return;
            }
            const col = Math.max(0, Math.min(gs - 1, Math.floor((e.clientX - rect.left) / cellWidth)));
            if (col !== current.col) {
                updateSelectedCell(col, current.row);
            }
        }
    }, [updateSelectedCell]);

    const handleGridSliderPointerUp = useCallback(() => {
        sliderDragAxis_ref.current = null;
        window.removeEventListener('pointermove', handleGridSliderPointerMove);
        window.removeEventListener('pointerup', handleGridSliderPointerUp);
        window.removeEventListener('pointercancel', handleGridSliderPointerUp);
    }, [handleGridSliderPointerMove]);

    const handleGridSliderPointerDown = (axis) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        sliderDragAxis_ref.current = axis;
        // Start from the middle if nothing is selected yet.
        if (!selectedCell_ref.current) {
            const midIndex = Math.floor(gridSize_ref.current / 2);
            updateSelectedCell(midIndex, midIndex);
        }
        window.addEventListener('pointermove', handleGridSliderPointerMove);
        window.addEventListener('pointerup', handleGridSliderPointerUp);
        window.addEventListener('pointercancel', handleGridSliderPointerUp);
    };

    // Make sure the window listeners are removed if the component unmounts mid-drag.
    useEffect(() => {
        return () => {
            window.removeEventListener('pointermove', handleGridSliderPointerMove);
            window.removeEventListener('pointerup', handleGridSliderPointerUp);
            window.removeEventListener('pointercancel', handleGridSliderPointerUp);
        };
    }, [handleGridSliderPointerMove, handleGridSliderPointerUp]);

    const findClawFeature = (_campaignData = null) => {
        if (_campaignData === null) {
            _campaignData = campaignData.current;
        }
        if (_campaignData && _campaignData.features) {
            const campaignData = _campaignData;
            console.log('Searching for claw feature in campaign data: ', campaignData.features);
            for (let i = 0; i < campaignData.features.length; i++) {
                if (campaignData.features[i].varKey === 'clawMachine') {
                    console.log('Claw feature found: ', campaignData.features[i]);
                    setClawFound(true);
                    setClawFeatureId(campaignData.features[i]._id);
                    const customisationData = customisationVars.current?.current?.[campaignData.features[i]._id] ?? null;
                    if (customisationData) {
                        fixSrcUrls(campaignData.features[i], campaignData.features[i]._id, campaignId, campaignData.features[i].customisation, customisationData);
                        PROCESS_CUSTOMISATION_VARS_AND_INJECT_COLOURS(customisationData, campaignData.features[i], campaignData);
                    }
                    setClawCustomisationData(customisationVars.current?.current?.[campaignData.features[i]._id] ?? null);
                    console.log('Claw customisation data: ', customisationVars.current?.current?.[campaignData.features[i]._id] ?? null);

                    if (customisationVars.current?.current?.[campaignData.features[i]._id]?.controller?.view?.content?.[0]?.useConfetti === true) {
                        CONFETTI_EFFECT_CONTROLLER.init(customisationVars.current?.current?.[campaignData.features[i]._id].controller.view.confettiColours, () => {console.log('Confetti effect ended')});
                    }

                    return campaignData.features[i];
                }
            }
        }
        return null;
    }
    useEffect(() => {
        if (!initialising) {
            const clawFeature = findClawFeature();
            if (clawFeature) {
                findClawDashboardVars();
                console.log('Claw feature is present in the campaign data.');
            } else {
                console.warn('No claw feature found in the campaign data.');
            }
        }
    }, [initialising]);

    const findClawDashboardVars = (_campaignData = null) => {
        console.log('Finding claw dashboard vars...');
        if (_campaignData === null) {
            _campaignData = campaignData.current;
        }
        console.log('Campaign data for finding claw dashboard vars: ', _campaignData);
        if (_campaignData && _campaignData.features) {
            const campaignData = _campaignData;
            for (let i = 0; i < campaignData.features.length; i++) {
                if (campaignData.features[i].varKey === 'clawMachine') {
                    console.log('Claw feature found for dashboard vars: ', campaignData.features[i]);
                    const dashboardVars = featureVars.current.current[campaignData.features[i]._id] ?? null;
                    setClawDashboardVars(dashboardVars);
                    console.log('Claw dashboard vars: ', dashboardVars);
                    return dashboardVars;
                }
            }
        }
        setClawDashboardVars(null);
        return null;
    }


    const frontendBaseUrl = window.location.origin;
    const backendBaseUrl = config.apiBaseUrl;
    const [campaignContext, setCampaignContext] = useContext(CampaignContext);

    const { campaignId, streamId, gamePIN } = useParams();

    const streamConnected = useRef(false);
    const checkConnectionStatus = (streamID) => {
        // console.log('Checking connection status...', streamId);
        axios
            .get(streamConnectionStatusRoute, {
                params: { streamID: streamId },
                withCredentials: true
            })
            .then(function (response) {
                // console.log("Stream is active!", response.data.active);
                streamConnected.current = response.data.active;
            })
            .catch(function (error) {
                streamConnected.current = false;
            });
    }

    useEffect(
        () => {
            checkConnectionStatus();
        },
        []
    )

    const socket = useRef(null);
    const connectToCampaignStudio = () => {
        if (socket.current === null) {
            const hostedSocketBaseUrl = window.location.hostname === 'dev.campaignstudio.tv'
                || window.location.hostname === 'api-dev.campaignstudio.tv'
                ? 'https://api-dev.campaignstudio.tv'
                : config.socketIoBaseUrl;

            socket.current = process.env.REACT_APP_NODE_ENV == 'local'
                ? io(process.env.REACT_APP_SOCKET_IO_BASE_URL)
                : io(hostedSocketBaseUrl, {
                    transports: ['polling'],  // Start with polling only
                    upgrade: true,            // Then upgrade to WebSocket
                    secure: true,
                    path: '/socket.io/',
                    reconnectionAttempts: 5,
                    reconnectionDelay: 1000,
                    timeout: 20000
                });

            console.log(socket.current, process.env.REACT_APP_SOCKET_IO_BASE_URL, process.env.REACT_APP_NODE_ENV);
            socket.current.on('connect', () => {
                console.log('Socket connected successfully!', socket.current.id);
                // console.log('Transport type:', socket.current.io.engine.transport.name);
                if (streamId) {
                    socket.current.emit('clawControllerJoinStream', streamId);
                    console.log('Campaign Studio Claw Controller: Connected');
                }
                else {
                    console.warn('Campaign Studio Claw Controller: Not Connected')
                }

            })

            socket.current.on('featureCommand', (messageObj) => {
                //console.log('Command Received: ', messageObj.command, messageObj.chatObj);
                // postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: messageObj.command, chatData: messageObj.chatObj });
                console.log('[Feature Socket] featureCommand received', {
                    featureID: messageObj.featureID,
                    command: messageObj.command,
                    commandParam: messageObj?.extraData?.commandParam,
                    commandParamType: typeof messageObj?.extraData?.commandParam,
                    extraData: messageObj?.extraData,
                });

                // If messageObj.command === 'LIVE_POSITION_VOTE' then we need to pull out messageObj.vote and messageObj.username and check if we need to filter it and log it
                if (messageObj.command === 'LIVE_POSITION_VOTE') {
                    const voteData = {
                        vote: messageObj?.extraData?.hashtag,
                        username: messageObj?.extraData?.username,
                    }
                    // If our pollOptions is empty or the vote is in the pollOptions then we can log it, otherwise we ignore it
                    // if (pollOptions.length === 0 || findInPollOptions(voteData.vote)) {
                        console.log('Vote received: ', voteData);
                        // Add to our liveVotes state, but restrict it to the last 20 votes
                        setLiveVotes((prevVotes) => [...prevVotes.slice(-19), voteData]);
                    // }
                }


                /* if (messageObj.featureID === 'GLOBAL') {
                    postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: messageObj.command, extraData: messageObj.extraData });
                } else {
                    postMessageToFrame(messageObj.featureID, POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: messageObj.command, extraData: messageObj.extraData });
                } */
            });
            socket.current.on('updateCustomisationVars', (data) => {
                console.log('CV CURRENT', data);
                customisationVars.current = data.updatedVars;
                // Pull out the claw customisation vars
                findClawFeature();
                
                // Using !== false so we can support the old way of not including this option, which means we want to report to the feature
                /* if (data.reportToFeature !== false) {
                    passFeatureCustomisationsToFeature(data.featureID)
                    //console.log('Command Received: ', messageObj.command, messageObj.chatObj);
                    // postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: messageObj.command, chatData: messageObj.chatObj });
                    //postMessageToFrame(messageObj.featureID, POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: messageObj.command, extraData: messageObj.extraData });
                } */
            });
            socket.current.on('updateFeatureVars', (updatedVars) => {
                console.log('Vars received: ', updatedVars);
                featureVars.current.current = updatedVars;
                findClawDashboardVars();
                /* if (ref_iframesLoaded.current) {
                    for (let prop in updatedVars) {
                        if (ref_iframesLoaded.current[prop]) {
                            // Make sure we have activated and demoMode in there (could prob do this better if we ever need more top level stuff)!
                            // All features can know about demo mode...
                            featureVars.current.current[prop].demoMode = featureVars.current.current.demoMode;
                            // But only csl knows about the top level activated and the advertiser, features will have their own activated var
                            if (prop === 'contentSceneLibrary') {
                                featureVars.current.current[prop].activated = featureVars.current.current.activated;
                                setCSLActivate(featureVars?.current?.current?.contentSceneLibrary?.overlayState.currentView == 'configoverlay');

                                featureVars.current.current[prop].adBugAdvertiser = featureVars.current.current.adBugAdvertiser;
                            }
                            if (updatedVars.fromFeature !== true) {
                                postMessageToFrame(prop, POST_MESSAGE_OUT_TYPES.SETTINGS_VARS_RECEIVED, featureVars.current.current[prop]);
                            }
                        }
                    }
                } */
            });
            socket.current.on('dashboardJoinStream', () => {
                console.log('A dashboard has joined!');
                // let the new dashboard know if we are already loaded
                /* checkIfFeatureReady();
                // let the new dashboard know if we are audio enabled
                if (audioEnabled.current) {
                    const timestamp = Date.now();
                    socket.current.emit('audioEnabled', { streamID: streamId, timestamp: timestamp });
                } */
            });
            socket.current.on("connect_error", (err) => {
                console.error('Socket connection error:', err);
                console.error('Error type:', typeof err);
                console.error('Error message:', err.message);
                console.error('Transport options:', socket.current.io.opts.transports);
            });

            socket.current.on('streamActivated', function (data) {
                // console.log('STREAM ACTIVATED')
                streamConnected.current = true;
            });
            socket.current.on('streamDeactivated', function (data) {
                // console.log('STREAM DEACTIVATED')
                streamConnected.current = false;
            });

            // --- Claw machine sockets ---
            socket.current.on('clawSwitchCam', (data) => {
                /* if (randomFeatureID.current == masterFeatureID.current) {
                    // console.log('Claw switch cam: ', data);
                    postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.CLAW_SWITCH_CAM, { camId: data });
                } */
            });

            socket.current.on('clawMoveEnded', (data) => {
                /* if (randomFeatureID.current == masterFeatureID.current) {
                    // console.log('Claw move ended: ', data);
                    postMessageToAllFrames(POST_MESSAGE_OUT_TYPES.CLAW_MOVE_ENDED, { winner: data });
                } */
            });

            // messages from the feature page
            socket.current.on("featureReady", (data) => {
                console.log('***************************** FEATURE READY', data);
                setMainFeaturePageLoaded(true);
                /* setFeatureReady(true);
                if (activeFeatureIds.current.indexOf(data.featureID) === -1) {
                    activeFeatureIds.current.push(data.featureID);
                    setActivatedFeatures(activeFeatureIds.current.length);
                }
                console.log('Feature list:', activeFeatureIds.current); */
            }
            
            );
            socket.current.on("featureUnloaded", (data) => {
                console.log('***************************** FEATURE UNLOAD', data);
                setMainFeaturePageLoaded(false);
                // Reset the claw controller state when the feature is unloaded
                setClawPhase(null);
                setYourSelectedPosition(null);
                setSelectedPosition(null);
                setLastResultWin(null);
                setLastResultSelectedPlayer(null);
                setLastResultSelectedPlayerChannel(null);
                setPollOptions([]);
                /* if (activeFeatureIds.current.indexOf(data.featureID) !== -1) {
                    activeFeatureIds.current = activeFeatureIds.current.filter((id) => id !== data.featureID);
                    setActivatedFeatures(activeFeatureIds.current.length);
                    if (activeFeatureIds.current.length === 0) {
                        setFeatureReady(false); 
                    }
                }
                if (!data.retainAudioState) {
                    setAudioEnabled(false);
                }
                console.log('Feature list:', activeFeatureIds.current); */
            });

            socket.current.on("reportPhaseChange", (data) => {
                console.log('***************************** FEATURE REPORT PHASE CHANGE', data);
                if (data.featureID === clawFeatureId_ref.current) {
                    console.log('Claw feature phase change reported: ', data.data?.phaseId, data.data?.phaseData);
                    setClawPhase(data.data?.phaseId);
                    setRawClawPhaseData(data.data?.phaseData);
                    if (data.data?.phaseData?.positionSelectMode) {
                        setGameMode(data.data?.phaseData?.positionSelectMode);
                    }
                    if (data.data?.phaseData?.gridSize) {
                        setGridSize(data.data?.phaseData?.gridSize);
                    }
                    if (data.data?.phaseData?.pollData) {
                        setPollOptions(data.data?.phaseData?.pollData || []);
                    }
                    // Update streamersTurn (and its ref) before the CREDIT_START logic below, since that same
                    // event can carry the new currentStreamersTurn and the state update wouldn't be visible yet.
                    if (data.data?.phaseData?.currentStreamersTurn) {
                        streamersTurn_ref.current = data.data?.phaseData?.currentStreamersTurn;
                        setStreamersTurn(data.data?.phaseData?.currentStreamersTurn);
                    }
                    if (data.data?.phaseId === CLAW_PHASE_IDS.CREDIT_START) {
                        // Reset the last result data when a new game starts
                        setLastResultWin(null);
                        setLastResultSelectedPlayer(null);
                        setLastResultSelectedPlayerChannel(null);
                        setSelectedPosition(null);
                        resetSelectedCell();
                        setYourSelectedPosition(null);
                        setLiveVotes([]);
                    }
                    if (data.data?.phaseId === CLAW_PHASE_IDS.WIN_PRIZE) {
                        setLastResultWin(true);
                        if (clawCustomisationData_ref.current?.controller?.view?.content?.[0]?.useConfetti === true) {
                            CONFETTI_EFFECT_CONTROLLER.start();
                            setTimeout(() => {
                                CONFETTI_EFFECT_CONTROLLER.end();
                            }, 6000);
                        }
                    }
                    if (data.data?.phaseId === CLAW_PHASE_IDS.WINNER_SELECTED) {
                        setLastResultSelectedPlayer(data.data?.phaseData?.selectedName || null);
                        setLastResultSelectedPlayerChannel(data.data?.phaseData?.channel || null);
                    }
                    if (data.data?.phaseId === CLAW_PHASE_IDS.LOSE) {
                        setLastResultWin(false);
                    }
                    if (data.data?.phaseId === CLAW_PHASE_IDS.PLAY_MOVE) {
                        setLastResultSelectedPlayer(data.data?.phaseData?.selectedPlayer || null);
                        setLastResultSelectedPlayerChannel(data.data?.phaseData?.selectedChannel || null);
                        setSelectedPosition(data.data?.phaseData?.selectedPosition || null);
                    }
                }
            });

            setInitialising(false);
        }
    }

    /** Make sure we have campiagn data - as this is it's own route we can't guarantee we do! */
    const campaignData = useRef(null);
    const pullInCampaignData = () => {
        if (campaignId) {
            axios
                .get(getCampaignOverviewRoute, { params: { campaignID: campaignId }, withCredentials: true })
                .then(async (response) => {
                    // console.log('Got campaign data: ', response.data.campaignData);
                    setCampaignContext((oldValues) => {
                        return { ...oldValues, initialising: false, campaignData: response.data.campaignData }
                    })
                    campaignData.current = response.data.campaignData;
                    getCustomisationVars();
                    getCampaignCustomisationVars(campaignId);

                    /* if (doWeHaveAnExpedition()) {
                        await kickStartExpedition();
                    } */
                })
                .catch((error) => {
                    console.error("Error loading campaign data:", error);
                    // Only navigate away if we're not in preview mode
                    /* if (!checkIsPreview()) {
                        navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGNS);
                    } else {
                        // In preview mode, we can show an error but stay on the page
                        toast.error("Preview Error: Invalid campaign ID");
                    } */
                });
        } else {
            // Same preview check for empty campaignId
            /* if (!checkIsPreview()) {
                navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGNS);
            } else {
                toast.error("Preview Error: No campaign ID specified");
            } */
        }
    }

    const findFeatureData = (campaignData, featureId) => {
        if (campaignData && campaignData.features) {
            for (let i = 0; i < campaignData.features.length; i++) {
                if (campaignData.features[i]._id === featureId) {
                    return campaignData.features[i];
                }
            }
        }
        return null;
    }

    const featureVars = useRef(null);
    const getFeatureVars = () => {
        console.log('Get Feature Vars');
        axios
            .get(getFeatureVarsRoute, {
                params: { campaignID: campaignId, streamID: streamId },
                withCredentials: true
            })
            .then(function (response) {
                if (response.data.streamVars) {
                    // Add in our base urls so our features can access them from the state in bootstrap.js
                    let baseUrls = {
                        frontend: frontendBaseUrl,
                        backend: backendBaseUrl,
                    }
                    for (let p in response.data.streamVars.current) {
                        if (typeof response.data.streamVars.current[p] === 'object') {
                            response.data.streamVars.current[p].baseUrls = baseUrls;
                        }
                    }
                    featureVars.current = response.data.streamVars;
                    console.log('Got streamVars: ', featureVars.current);
                    resetOnLoadDefaultFeatureVarsInternal();
                    getCampaignLevelFeatureVars();
                    findClawDashboardVars();
                }
            })
            .catch(function (error) {
                // toast.error('Error - ' + error);
            });
    }

    const resetOnLoadDefaultFeatureVarsInternal = () => {
        // Reset any featureVars that need to be reset on load...
        const _campaignData = campaignData.current || campaignContext.campaignData;
        // console.log('RESET ONLOAD VARS 1: ', _campaignData.features, _campaignData.features.length);
        if (_campaignData && _campaignData.features) {
            for (let i = 0; i < _campaignData.features.length; i++) {
                // console.log('RESET ONLOAD VARS: ', _campaignData.features[i]);
                let madeChanges = resetOnLoadDefaultFeatureVars(featureVars.current, _campaignData.features[i], customisationVars.current);
            }
        } else {
            setTimeout(() => resetOnLoadDefaultFeatureVarsInternal(), 20);
        }
    }

    const campaignLevelFeatureVars = useRef(null);
    const getCampaignLevelFeatureVars = () => {
        // console.log('Get Campaign Level Feature Vars');
        axios
            .get(getCampaignFeatureVarsRoute, {
                params: { campaignID: campaignId },
                withCredentials: true
            })
            .then(function (response) {
                if (response.data.streamVars) {
                    campaignLevelFeatureVars.current = response.data.streamVars;
                    // console.log('Got campaignLevelFeatureVars: ', campaignLevelFeatureVars.current);

                    // This is where we will pull out multi stream data and inject it into the stream level vars
                    pullOutMultiStreamData();

                    // setupIframes();
                    connectToCampaignStudio();
                }
            })
            .catch(function (error) {
                // toast.error('Error - ' + error);
            });
    }

    const customisationVars = useRef(null);
    const getCustomisationVars = () => {
        // console.log('Get Customisation Vars');
        axios
            .get(getStreamCustomisationVarsRoute, {
                params: { campaignID: campaignId, streamID: streamId },
                withCredentials: true
            })
            .then(function (response) {
                if (response.data.customisationVars) {
                    sanitizeCustomisationData(campaignData.current, response.data.customisationVars.current);
                    customisationVars.current = response.data.customisationVars;
                    console.log('Got customisationVars: ', customisationVars.current);
                    getFeatureVars();
                }
            })
            .catch(function (error) {
                // toast.error('Error - ' + error);
            });
    }

    const campaignLevelCustomisationVars = useRef(null);
    const getCampaignCustomisationVars = () => {
        // console.log('Get Campaign Customisation Vars');
        axios
            .get(getCampaignCustomisationVarsRoute, {
                params: { campaignID: campaignId },
                withCredentials: true
            })
            .then(function (response) {
                if (response.data.customisationVars) {
                    sanitizeCustomisationData(campaignData.current, response.data.customisationVars.current);
                    campaignLevelCustomisationVars.current = response.data.customisationVars;
                    // console.log('Got campaignLevelCustomisationVars: ', campaignLevelCustomisationVars.current);
                }
            })
            .catch(function (error) {
                // toast.error('Error - ' + error);
            });
    }

    useEffect(
        () => {
            // The flow for this will be:
            //      getFeatureVars
            //      setupIframes
            // on frame loaded:
            //      passFeatureVarsToFrame
            // on all frames loaded:
            //      connectToCampaignStudio
            // we probably need to handle unloading as well...
            // getCustomisationVars();
            pullInCampaignData();
        }, [streamId, campaignId]
    )


    // Multi stream tag is a string derived from the channel list in the stream data, which is already sorted alphabetically.
    // This way we can specify which data needs to be shared between streams (only streams with the same channel list).
    const multiStreamTag = useRef(null);
    const getMultiStreamTag = () => {
        if (multiStreamTag.current === null) {
            const channelList = campaignData.current.streams.find(stream => stream._id === streamId).channels;
            let tag = '';
            for (let i = 0; i < channelList.length; i++) {
                tag += channelList[i].twitchHandle.toLowerCase();
            }
            multiStreamTag.current = tag;
        }
        return multiStreamTag.current;
    }

    const pullOutMultiStreamData = () => {
        const multiStreamTag = getMultiStreamTag();
        for (let prop in campaignLevelFeatureVars.current.current) {
            if (campaignLevelFeatureVars.current.current[prop].multiStreamData && campaignLevelFeatureVars.current.current[prop].multiStreamData[multiStreamTag]) {
                const taggedData = campaignLevelFeatureVars.current.current[prop].multiStreamData[multiStreamTag];
                for (let prop2 in taggedData) {
                    // Deep copy the data into the stream level vars
                    featureVars.current.current[prop][prop2] = JSON.parse(JSON.stringify(campaignLevelFeatureVars.current.current[prop].multiStreamData[multiStreamTag][prop2]));
                    // console.log('Multi stream data pulled out: ', prop, campaignLevelFeatureVars.current.current[prop].multiStreamData, multiStreamTag, prop2, featureVars.current.current[prop][prop2]);
                }
            }
        }
    }

    // Converts a "#J-17" style position string back into 0-based col/row indices.
    const parsePositionString = (positionString, gs) => {
        const match = /^#([A-Za-z]+)-(\d+)$/.exec(positionString ?? '');
        if (!match) {
            return null;
        }
        const col = GRID_LETTERS.indexOf(match[1].toUpperCase());
        const row = gs - parseInt(match[2], 10);
        if (col < 0 || row < 0 || row >= gs) {
            return null;
        }
        return { col, row };
    }

    // Positions (as % of the camera feed) for each available poll option, on the live camera view.
    // COORDINATE_CONVERTOR.applyPerspective is calibrated against the grid overlay image at its
    // native/full size, so its output is scaled using the grid image's own rendered box (gridImageBox)
    // rather than the camera feed container's, since the image is letterboxed ("contain") within it.
    const convertGridPositionToPercent = (col, row) => {
        if (!gridImageBox || !cameraFeedSize.width || !cameraFeedSize.height) {
            return null;
        }
        // Scaled per-axis (rather than a single width-derived ratio) so this stays correct even if
        // the reference image's rendered box isn't perfectly aspect-locked to its natural size.
        const scaleX = gridImageBox.width / gridImageBox.naturalWidth;
        const scaleY = gridImageBox.height / gridImageBox.naturalHeight;
        const { x, y } = COORDINATE_CONVERTOR.applyPerspective(col, row, gridSize);
        const leftPx = gridImageBox.left + gridImageBox.width / 2 + x * scaleX;
        const topPx = gridImageBox.top + gridImageBox.height - y * scaleY;
        return {
            leftPercent: (leftPx / cameraFeedSize.width) * 100,
            topPercent: (topPx / cameraFeedSize.height) * 100,
        };
    }

    // Sliders only make sense when a single person is choosing (individual/streamer); team mode picks
    // from the machine-generated poll options instead, via the button row/clickable markers below.
    const useSliderSelection = gameMode === 'individual' || gameMode === 'streamer';

    const liveGridMarkers = pollOptions.reduce((markers, pollOption) => {
        const parsed = parsePositionString(pollOption.option, gridSize);
        const percent = parsed && convertGridPositionToPercent(parsed.col, parsed.row);
        if (!percent) {
            return markers;
        }
        markers.push({ key: pollOption.option, tag: pollOption.option, col: parsed.col, row: parsed.row, ...percent });
        return markers;
    }, []);

    // Tapping the live camera feed selects whichever grid cell's converted position is closest to the
    // tap, since there's no per-cell hit target on the raw camera feed. Slider modes only - in team/poll
    // modes selection instead happens by tapping a poll option marker (or button) directly.
    const handleLiveGridClick = (e) => {
        if (!cameraFeedEl) {
            return;
        }
        const rect = cameraFeedEl.getBoundingClientRect();
        // The grid overlay (and its markers) are rendered inside a layer that's shifted by
        // liveGridYOffset via CSS transform, but convertGridPositionToPercent works in the layer's
        // un-shifted coordinate space - so the raw click position needs the same offset removed.
        const liveGridYOffsetPercent = rect.height ? (liveGridYOffset / rect.height) * 100 : 0;
        const clickXPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const clickYPercent = ((e.clientY - rect.top) / rect.height) * 100 - liveGridYOffsetPercent;
        const candidateCells = [];
        for (let row = 0; row < gridSize; row++) {
            for (let col = 0; col < gridSize; col++) {
                if (!isPositionUnusable(gridSize, col, row)) {
                    candidateCells.push({ col, row });
                }
            }
        }
        let closestCell = null;
        let closestDistSq = Infinity;
        for (const candidate of candidateCells) {
            const percent = convertGridPositionToPercent(candidate.col, candidate.row);
            if (!percent) {
                continue;
            }
            const dx = percent.leftPercent - clickXPercent;
            const dy = percent.topPercent - clickYPercent;
            const distSq = dx * dx + dy * dy;
            if (distSq < closestDistSq) {
                closestDistSq = distSq;
                closestCell = candidate;
            }
        }
        if (!closestCell) {
            return;
        }
        // Reject taps that land well outside the grid, by comparing the tap's distance to the closest
        // cell against that cell's spacing to its neighbour (accounts for the perspective grid's cells
        // getting smaller/closer together towards the back).
        const closestPercent = convertGridPositionToPercent(closestCell.col, closestCell.row);
        const neighborCol = closestCell.col < gridSize - 1 ? closestCell.col + 1 : closestCell.col - 1;
        const neighborPercent = convertGridPositionToPercent(neighborCol, closestCell.row);
        if (closestPercent && neighborPercent) {
            const spacingDx = neighborPercent.leftPercent - closestPercent.leftPercent;
            const spacingDy = neighborPercent.topPercent - closestPercent.topPercent;
            const spacingDistSq = spacingDx * spacingDx + spacingDy * spacingDy;
            // Half the cell spacing is the furthest a tap can be while still landing "in" that cell.
            if (closestDistSq > spacingDistSq / 4) {
                return;
            }
        }
        updateSelectedCell(closestCell.col, closestCell.row);
    };

    // Slider-based selection has no matching poll option, so it needs its own marker on the live camera view.
    const liveSelectedMarker = (() => {
        if (!useSliderSelection || !selectedCell) {
            return null;
        }
        const percent = convertGridPositionToPercent(selectedCell.col, selectedCell.row);
        if (!percent) {
            return null;
        }
        const tag = `#${GRID_LETTERS[selectedCell.col]}-${gridSize - selectedCell.row}`;
        return { key: `selected-${tag}`, tag, ...percent };
    })();

    // Temporary calibration aid: fixed markers on the 4 grid corners, so the perspective conversion
    // can be checked/tuned against the actual camera feed. Remove once COORDINATE_CONVERTOR is correct.
    const REFERENCE_CORNER_POSITIONS = [
        { col: 0, row: gridSize - 1 },
        { col: gridSize - 1, row: gridSize - 1 },
        { col: 0, row: 0 },
        { col: gridSize - 1, row: 0 },
    ];
    const referenceCornerMarkers = REFERENCE_CORNER_POSITIONS.reduce((markers, { col, row }) => {
        const percent = convertGridPositionToPercent(col, row);
        if (!percent) {
            return markers;
        }
        const tag = `#${GRID_LETTERS[col]}-${gridSize - row}`;
        markers.push({ key: `reference-${tag}`, tag, ...percent });
        return markers;
    }, []);

    const controllerContent = clawCustomisationData?.controller?.view?.content?.[0] ?? {};
    const gridLineColour = clawCustomisationData?.inGameVisuals?.positionSelect?.playerGrid?.[0]?.colour ?? '#1ed6ff';
    const positionMarkersCustomisationData = clawCustomisationData?.inGameVisuals?.positionSelect?.positionMarkers?.[0] ?? {};
    const controllerVidsCustomisation = clawCustomisationData?.controller?.view?.videos?.[0] ?? {};
    const controllerStyles = {
        '--claw-phase-border-width': `${controllerContent.lineThickness ?? 2}px`,
        '--claw-phase-border-color': controllerContent.borderColour ?? '#d61ed6',
        '--claw-phase-tab-color': controllerContent.borderColour ?? '#d61ed6',
        '--claw-button-active-color': controllerContent.buttonColour1 ?? '#1ed6ff',
        '--claw-button-inactive-color': controllerContent.buttonColour2 ?? 'transparent',
        '--claw-button-font': controllerContent.buttonFont ?? 'Roboto, Arial',
        '--claw-button-font-color': controllerContent.fontColour ?? '#fff',
        '--claw-button-font-size': `${controllerContent.fontSize ?? 11}px`,
        '--claw-button-font-size-small': `${(controllerContent.fontSize ?? 11) * 0.7}px`,
        '--claw-button-font-weight': controllerContent.isBold ? 700 : 400,
        '--claw-tab-font': controllerContent.tabFont ?? 'Roboto, Arial',
        '--claw-tab-font-color': controllerContent.tabFontColour ?? '#fff',
        '--claw-tab-font-size': `${controllerContent.tabFontSize ?? 14}px`,
        '--claw-tab-font-size-small': `${(controllerContent.tabFontSize ?? 14) * 0.7}px`,
        '--claw-tab-font-weight': controllerContent.tabIsBold ? 700 : 400,
        '--claw-main-font': controllerContent.mainFont ?? 'Roboto, Arial',
        '--claw-main-font-size': `${controllerContent.mainFontSize ?? 14}px`,
        '--claw-main-font-size-small': `${(controllerContent.mainFontSize ?? 14) * 0.7}px`,
        '--claw-main-font-weight': controllerContent.mainIsBold ? 700 : 400,
        '--claw-main-font-color': controllerContent.mainTextColour ?? '#fff',

        "--pointMarkerColour": positionMarkersCustomisationData.markerColour,
        "--markerOpacity": positionMarkersCustomisationData.markerOpacity / 100,
        "--markerVisibility": positionMarkersCustomisationData.markerOpacity > 0 ? 'visible' : 'hidden',
        "--tagOpacity": positionMarkersCustomisationData.bgOpacity / 100,
        "--markerFont": positionMarkersCustomisationData.font,
        "--markerFontSize": positionMarkersCustomisationData.fontSize + 'px',
        "--markerFontColour": positionMarkersCustomisationData.fontColour,
        "--markerFontWeight": positionMarkersCustomisationData.isBold ? 'bold' : 'normal',

        "--playingMoveVidScale": controllerVidsCustomisation.playingVidEffectScale ? controllerVidsCustomisation.playingVidEffectScale + '%' : '100%',
        "--playingMoveVidXOffset": !isNaN(controllerVidsCustomisation.playingVidEffectXOff) ? (50 + controllerVidsCustomisation.playingVidEffectXOff / 2) + '%' : '0%',
        "--playingMoveVidYOffset": !isNaN(controllerVidsCustomisation.playingVidEffectYOff) ? (50 + controllerVidsCustomisation.playingVidEffectYOff / 2) + '%' : '0%',
    };

    const PosMarker = (props) => {
        const markerSettings = props?.settings ?? clawCustomisationData?.inGameVisuals?.positionSelect?.positionMarkers?.[0] ?? {};
        // Matches the "active" poll option button's colour, so the selected live-grid marker (team mode) reads as the same selection.
        const markerFillColour = props.highlighted ? 'var(--claw-button-active-color, #1ed6ff)' : markerSettings.bgColour;

        const posMarkerStyle = {
            left: props.x + 'px',
            top: props.y + 'px',
        };

        const getMarkerBox = () => {
            return (
                <svg width="106" height="58" viewBox="0 0 106 58" xmlns="http://www.w3.org/2000/svg">
                    {/* Insets the box 20px from the top and 20px from each side, keeping the pointer tip anchored at (53, 58) */}
                    <g transform="translate(20, 20) scale(0.6226415094339623, 0.6551724137931034)">
                        <path d="M0 40.1539V6.69232C0 4.91741 0.695355 3.21519 1.93309 1.96014C3.17084 0.705082 4.84957 0 6.6 0H99.4C101.15 0 102.829 0.705082 104.067 1.96014C105.305 3.21519 106 4.91741 106 6.69232V40.1539C106 41.0328 105.829 41.903 105.498 42.715C105.166 43.5269 104.68 44.2647 104.067 44.8861C103.454 45.5075 102.726 46.0005 101.926 46.3368C101.125 46.6731 100.267 46.8462 99.4 46.8462H64.55C64.0377 46.8462 63.5324 46.9672 63.0742 47.1995C62.616 47.4318 62.2174 47.7691 61.91 48.1847L55.64 56.6615C55.3326 57.0771 54.934 57.4144 54.4758 57.6467C54.0176 57.8791 53.5123 58 53 58C52.4877 58 51.9824 57.8791 51.5242 57.6467C51.066 57.4144 50.6674 57.0771 50.36 56.6615L44.09 48.1847C43.7826 47.7691 43.384 47.4318 42.9258 47.1995C42.4676 46.9672 41.9623 46.8462 41.45 46.8462H6.6C4.84957 46.8462 3.17084 46.1412 1.93309 44.8861C0.695355 43.6311 0 41.9288 0 40.1539Z" fill={markerFillColour}/>
                    </g>
                </svg>
            )
        }

        return (
            <>
                <div className={`claw-controller-point-marker`} style={posMarkerStyle}></div>
                <div className={`claw-controller-position-marker-holder`} style={posMarkerStyle}>
                    <div className={`claw-controller-position-marker`}>
                        <div className='claw-controller-marker-box'>
                            {getMarkerBox()}
                        </div>
                        <div className='claw-controller-marker-text'>
                            {props.tag?.substring(1)}
                        </div>
                    </div>
                </div>
            </>
        )
    }

    const ClawOptionsGrid = (props) => {
        // Block of unusable cells sits in the bottom-left; grid has an extra label column (numbers), hence the +1.
        const unusableCount = UNUSABLE_GRID_COUNTS[`_${gridSize}x${gridSize}`] ?? 0;
        const totalCells = gridSize + 1;
        // The marker sits on the actual selection, or the middle of the grid when nothing is selected.
        const midIndex = Math.floor(gridSize / 2);
        const activeCol = Math.min(selectedCell?.col ?? midIndex, gridSize - 1);
        const activeRow = Math.min(selectedCell?.row ?? midIndex, gridSize - 1);

        return (
            <div className={`claw-poll-positions-selector${useSliderSelection ? '' : ' team-mode'}`} ref={clawGridRef} style={{
                width: '100%',
                height: '100%',
                '--claw-grid-line-color': gridLineColour,
                '--claw-not-available-image': `url(${notAvailableImage})`,
            }}>
                {[...Array(gridSize)].map((_, rowIndex) => (
                    <div className="position-selector-row" key={rowIndex}>
                        {[...Array(gridSize)].map((_, colIndex) => {
                            const useablePos = !isPositionUnusable(gridSize, colIndex, rowIndex);
                            // const selectablePos = selectedPositions.length < MAX_SELECTABLE || selectedPositions.includes(positionString);
                            // const selectedPos = selectedPositions.includes(positionString);
                            const isSelectedRow = selectedCell && rowIndex === selectedCell.row;
                            const isSelectedCol = selectedCell && colIndex === selectedCell.col;
                            const isSelectedCell = isSelectedRow && isSelectedCol;
                            return(
                                <div className={`position-selector-box${colIndex === gridSize - 1 ? ' last-in-row' : ''}${useablePos && rowIndex === gridSize - 1 ? ' last-row' : ''}${!useablePos ? ' unavailable' : ''}${isSelectedCell ? ' selected-cell' : (isSelectedRow || isSelectedCol) ? ' selected-line' : ''}`} 
                                    key={colIndex}
                                    onClick={(e) => {
                                        // Team mode only allows selecting via the poll option markers/buttons, not anywhere on the grid.
                                        if (!useSliderSelection) {
                                            return;
                                        }
                                        // Select the square (also mirrors the position into the vote input box)
                                        updateSelectedCell(colIndex, rowIndex);
                                        // let newSelectedPositions = [...selectedPositions];
                                        /* if (newSelectedPositions.includes(positionString)) {
                                            newSelectedPositions = newSelectedPositions.filter(pos => pos !== positionString);
                                        } else {
                                            if (newSelectedPositions.length < MAX_SELECTABLE) {
                                                newSelectedPositions.push(positionString);
                                            }
                                        }
                                        setSelectedPositions(newSelectedPositions);
                                        props.sendFeatureCommandFunc(e, 'FORCE_POSITION_POLL_OPTIONS', props.featureId, {options: newSelectedPositions}); */
                                    }}
                                    /* onMouseEnter={useablePos && selectablePos ? (e) => {
                                        tooltipTimeoutRef.current = setTimeout(() => showToolTip(e, colIndex, rowIndex), 100);
                                    } : null}
                                    onMouseLeave={(e) => {
                                        clearTimeout(tooltipTimeoutRef.current);
                                        hideToolTip(e);
                                    }} */
                                >
                                    {/*(lastPollOptions.includes(positionString) || lastSelectedOption === positionString) &&
                                        <div className={`previous-poll-option${lastPollOptions.includes(positionString) ? ' in-poll' : ''}${lastSelectedOption === positionString ? ' chosen' : ''}${lastSelectedOption !== positionString && lastPollOptions.includes(positionString) && lastForcePollOptions.includes(positionString) ? ' forced' : ''}`}></div>
                                    */}
                                </div>
                            )
                        })}
                        <div className={`label-box row-number-label${rowIndex === gridSize - 1 ? ' last-row' : ''}${selectedCell && rowIndex === selectedCell.row ? ' selected-line' : ''}`}>
                            {(gridSize - rowIndex).toString()}
                        </div>
                    </div>
                ))}
                <div className="position-selector-row">
                    {[...Array(gridSize)].map((_, colIndex) => (
                        <div className={`label-box${selectedCell && colIndex === selectedCell.col ? ' selected-line' : ''}`} key={colIndex}>
                            {GRID_LETTERS[colIndex]}
                        </div>
                    ))}
                    <div className="label-box">
                        {' '}
                    </div>
                </div>
                {/* Available poll-option positions: clicking a marker selects that cell, same as clicking its grid square. */}
                {pollOptions.map((pollOption) => {
                    const parsed = parsePositionString(pollOption.option, gridSize);
                    if (!parsed || isPositionUnusable(gridSize, parsed.col, parsed.row)) {
                        return null;
                    }
                    return (
                        <div
                            key={`poll-option-marker-${pollOption.option}`}
                            className="claw-grid-poll-marker-anchor"
                            style={{
                                left: `${((parsed.col + 0.5) / totalCells) * 100}%`,
                                top: `${((parsed.row + 0.5) / totalCells) * 100}%`,
                            }}
                            onClick={() => updateSelectedCell(parsed.col, parsed.row)}
                        >
                            <PosMarker
                                x={0}
                                y={0}
                                tag={pollOption.option}
                                highlighted={!useSliderSelection && selectedCell?.col === parsed.col && selectedCell?.row === parsed.row}
                            />
                        </div>
                    );
                })}
                {/* Selected square marker: anchored to the cell's centre with % (so it tracks resizes);
                    PosMarker's px-based elements hang off this zero-size point. Slider mode only - in team
                    mode the matching poll-option marker is highlighted instead. */}
                {useSliderSelection && selectedCell &&
                    <div
                        className="claw-grid-selected-marker-anchor"
                        style={{
                            left: `${((activeCol + 0.5) / totalCells) * 100}%`,
                            top: `${((activeRow + 0.5) / totalCells) * 100}%`,
                        }}
                    >
                        <PosMarker x={0} y={0} tag={`#${GRID_LETTERS[activeCol]}-${gridSize - activeRow}`} />
                    </div>
                }
                {useSliderSelection &&
                    <>
                        {/* Right-edge slider: drag vertically to choose the row. Centred on the grid frame's border so it overlaps it. */}
                        <div
                            className="claw-grid-slider claw-grid-slider-right"
                            style={{
                                top: `${((activeRow + 0.5) / totalCells) * 100}%`,
                                width: `${100 / totalCells}%`,
                                height: `${100 / totalCells}%`,
                            }}
                            onPointerDown={handleGridSliderPointerDown('row')}
                        >
                            <img src={selectorImage} alt="" draggable="false" />
                        </div>
                        {/* Bottom-edge slider: drag horizontally to choose the column. Same asset, rotated to face up into the grid. */}
                        <div
                            className="claw-grid-slider claw-grid-slider-bottom"
                            style={{
                                left: `${((activeCol + 0.5) / totalCells) * 100}%`,
                                width: `${100 / totalCells}%`,
                                height: `${100 / totalCells}%`,
                            }}
                            onPointerDown={handleGridSliderPointerDown('col')}
                        >
                            <img src={selectorImage} alt="" draggable="false" />
                        </div>
                    </>
                }
                {unusableCount > 0 &&
                    <div
                        className="claw-grid-not-available-block"
                        style={{
                            left: 0,
                            // Shift down 1px so the grid line above the block stays at full colour, rather than being dimmed by the backdrop.
                            top: `calc(${((gridSize - unusableCount) / totalCells) * 100}% + 1px)`,
                            width: `${(unusableCount / totalCells) * 100}%`,
                            height: `calc(${(unusableCount / totalCells) * 100}% - 1px)`,
                        }}
                    >
                        <div className="claw-grid-not-available-bottom-border" />
                        <div className="claw-grid-not-available-backdrop" />
                        <div
                            className="claw-grid-not-available-overlay"
                            style={{
                                // Inset by 1 grid square on every side, so there's a border of space around the icon.
                                top: `${100 / unusableCount}%`,
                                left: `${100 / unusableCount}%`,
                                right: `${100 / unusableCount}%`,
                                bottom: `${100 / unusableCount}%`,
                            }}
                        />
                    </div>
                }
                {/* <div className="poll-legend">
                    <div className="poll-legend-item">
                        <div className="color-box">
                            <div className="previous-poll-option in-poll"></div>
                        </div>
                        <div className="grow">In Last Poll</div>
                    </div>
                    <div className="poll-legend-item">
                        <div className="color-box">
                            <div className="previous-poll-option in-poll forced"></div>
                        </div>
                        <div className="grow">Forced Position</div>
                    </div>
                    <div className="poll-legend-item">
                        <div className="color-box">
                            <div className="previous-poll-option in-poll chosen"></div>
                        </div>
                        <div className="grow">Last Played Position</div>
                    </div>
                </div> */}
            </div>
        )
    }

    let bgVidSrc = clawCustomisationData?.controller?.view?.bg?.[0]?.vidBg?.srcUrl || "";
    if (SHOW_CAM_PHASES.includes(clawPhase)) {
        if (clawCustomisationData?.controller?.view?.bg?.[0]?.switchBgWhenPlaying && clawCustomisationData?.controller?.view?.bg?.[0]?.playingVidBg?.srcUrl) {
            bgVidSrc = clawCustomisationData?.controller?.view?.bg?.[0]?.playingVidBg?.srcUrl;
        }
    }
    let letsPlayTextVid = null;
    if (clawCustomisationData?.controller?.view?.content?.[0]?.useLetsPlayVid && clawCustomisationData?.controller?.view?.content?.[0]?.letsPlayVid?.srcUrl) {
        letsPlayTextVid = clawCustomisationData?.controller?.view?.content?.[0]?.letsPlayVid?.srcUrl;
    }

    return (
        <div className="claw-controller-viewport" ref={viewportRef} onClickCapture={handleViewportClickCapture}>
            {showNameModal && (
                <div className="claw-name-modal-overlay">
                    <div
                        className="claw-phase-frame claw-name-modal-frame"
                        style={{ ...controllerStyles, backgroundColor: clawCustomisationData?.controller?.view?.bg?.[0]?.colour }}
                    >
                        <div className="claw-phase-frame-header">
                            <div className="claw-phase-tab">Enter Your Name</div>
                            <div className="claw-phase-frame-actions" />
                        </div>
                        <div className="claw-phase-frame-body">
                            <input
                                type="text"
                                className="claw-name-modal-input"
                                value={nameInputValue}
                                onChange={(e) => setNameInputValue(e.target.value)}
                                maxLength={24}
                            />
                            <button
                                type="button"
                                className="claw-confirm-selection-button"
                                onClick={() => {
                                    setControllerName(nameInputValue.trim() || "Claw Controller");
                                    setShowNameModal(false);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="claw-controller-portrait-frame" style={{ backgroundColor: clawCustomisationData?.controller?.view?.bg?.[0]?.colour }}>
                {clawCustomisationData?.controller?.view?.bg?.[0]?.addVid === true && bgVidSrc && (
                    <video
                        className="claw-controller-bg-video"
                        src={bgVidSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                )}
                <div className="claw-controller-frame-content">
                    <div className="claw-controller-top-content-holder">
                        {clawCustomisationData?.controller?.view?.content?.[0]?.logo?.srcUrl && (
                            <img className="claw-controller-logo" src={clawCustomisationData.controller.view.content[0].logo.srcUrl} alt="Logo" />
                        )}
                    </div>
                    <div className="fl-column claw-controller-info-panel">
                        {(initialising || !clawFound || !mainFeaturePageLoaded) && (
                            <>
                                <div className="claw-phase-frame" style={controllerStyles}>
                                    <div className="claw-phase-frame-box">
                                        <div className="claw-phase-frame-header">
                                            <div className="claw-phase-tab">Initializing</div>
                                            <div className="claw-phase-frame-actions" />
                                        </div>
                                        <div className="claw-phase-frame-body" ref={cameraFeedRef}>
                                            <BasicMachineCameraFeed machineID={clawCustomisationData?.setup?.settings?.game[0]?.machine === 'left' ? 1 : 2} monochrome cameraYOffsetLevel={cameraYOffsetLevel} />
                                                        {/* Temporary grid for testing purposes */}
                                                        {/*
                                                        <div className="claw-grid-camera-gradient" />
                                                        <div
                                                            className="claw-grid-camera-overlay"
                                                            style={{
                                                                backgroundColor: gridLineColour,
                                                                WebkitMaskImage: `url(${GRID_OVERLAY_IMAGES[gridSize] ?? grid20x20Image})`,
                                                                maskImage: `url(${GRID_OVERLAY_IMAGES[gridSize] ?? grid20x20Image})`,
                                                            }}
                                                        />
                                                        */}
                                                        {/* Invisible, only used to measure the tinted overlay's actual rendered box for marker placement */}
                                                        {/*
                                                        <img
                                                            ref={gridImageRefCallback}
                                                            className="claw-grid-camera-reference-image"
                                                            src={GRID_OVERLAY_IMAGES[gridSize] ?? grid20x20Image}
                                                            alt=""
                                                            aria-hidden="true"
                                                        />
                                                        {referenceCornerMarkers.map((marker) => (
                                                            <div
                                                                key={marker.key}
                                                                className="claw-grid-camera-marker-anchor"
                                                                style={{ left: `${marker.leftPercent}%`, top: `${marker.topPercent}%` }}
                                                            >
                                                                <PosMarker x={0} y={0} tag={marker.tag} />
                                                            </div>
                                                        ))}
                                                        */}

                                        </div>
                                    </div>
                                </div>
                                {/*
                                    {clawFound && !initialising && !mainFeaturePageLoaded && (
                                        <h2>Waiting for main obs page to load</h2>
                                    )}
                                    {!clawFound && !initialising && (
                                        <h2>No claw feature found in the campaign data</h2>
                                    )}
                                    {initialising && (
                                        <h2>Loading campaign data...</h2>
                                    )}
                                    <p>Campaign ID: {campaignId}</p>
                                    <p>Stream ID: {streamId}</p>
                                    <p>Initialising: {initialising ? 'true' : 'complete'}</p>
                                    <p>Claw Found: {clawFound ? 'true' : 'false'}</p>
                                    <p>Main Feature Page Loaded: {mainFeaturePageLoaded ? 'true' : 'false'}</p>
                                */}

                                <div className="claw-controller-bottom-content-holder">
                                    {controllerContent.showSponsor === true && controllerContent.sponsorImage?.srcUrl && (
                                        <img className="claw-controller-sponsor-image" src={controllerContent.sponsorImage.srcUrl} alt="Sponsor" />
                                    )}
                                </div>
                            </>
                        )}
                        {clawFound && !initialising && mainFeaturePageLoaded && (
                            <div className="claw-phase-content-wrapper">
                                {/* <p>Claw feature is present and loaded!</p> */}
                                {(!clawPhase || IDLE_PHASES.includes(clawPhase)) && (
                                    <>
                                        <div className="claw-phase-frame" style={controllerStyles}>
                                            <div className="claw-phase-frame-box">
                                                <div className="claw-phase-frame-header">
                                                    <div className="claw-phase-tab">Waiting for Claw Machine</div>
                                                    {/* <div className="claw-phase-frame-actions">
                                                        <button
                                                            type="button"
                                                            className={`cam-view-toggle-button${idleCamView === 'front' ? ' active' : ''}`}
                                                            onClick={() => setIdleCamView('front')}
                                                        >
                                                            Front View
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`cam-view-toggle-button${idleCamView === 'top' ? ' active' : ''}`}
                                                            onClick={() => setIdleCamView('top')}
                                                        >
                                                            Top View
                                                        </button>
                                                    </div> */}
                                                </div>
                                                <div className="claw-phase-frame-body">
                                                    <BasicMachineCameraFeed machineID={clawCustomisationData?.setup?.settings?.game[0]?.machine === 'left' ? 1 : 2} cameraId={'front'} cameraYOffsetLevel={cameraYOffsetLevel} />
                                                    {/* <video
                                                        key={clawCustomisationData.controller.view.videos[0].playingMoveEffectVid.srcUrl}
                                                        className="claw-playing-overlay-video"
                                                        src={clawCustomisationData.controller.view.videos[0].playingMoveEffectVid.srcUrl}
                                                        autoPlay
                                                        playsInline
                                                        loop
                                                    /> */}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="claw-controller-bottom-content-holder">
                                            <h1>One Moment...</h1>
                                            {controllerContent.showSponsor === true && controllerContent.sponsorImage?.srcUrl && (
                                                <img className="claw-controller-sponsor-image" src={controllerContent.sponsorImage.srcUrl} alt="Sponsor" />
                                            )}
                                        </div>
                                    </>
                                )}
                                {clawPhase === CLAW_PHASE_IDS.POSITION_SELECT && 
                                    <>
                                        <div className="claw-phase-frame" style={controllerStyles}>
                                            <div className="claw-phase-frame-box">
                                                <div className="claw-phase-frame-header">
                                                    <div className="claw-phase-tab">Select Your Move</div>
                                                    <div className="claw-phase-frame-actions">
                                                        <button
                                                            type="button"
                                                            className={`cam-view-toggle-button${optionSelectView === 'grid' ? ' active' : ''}`}
                                                            onClick={() => setOptionSelectView('grid')}
                                                        >
                                                            Grid Select
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`cam-view-toggle-button${optionSelectView === 'live' ? ' active' : ''}`}
                                                            onClick={() => setOptionSelectView('live')}
                                                        >
                                                            Live Grid
                                                        </button>
                                                    </div>
                                                </div>
                                                {/* overflow: visible so the grid selector sliders can overlap the frame border */}
                                                <div className="claw-phase-frame-body" style={{ overflow: 'visible' }}>
                                                    {/* Camera feed kept mounted (to preserve space/state); shown on top when viewing the live grid, hidden otherwise */}
                                                    <div
                                                        className={`position-select-camera-feed${optionSelectView === 'live' ? ' active' : ''}`}
                                                        ref={cameraFeedRef}
                                                    >
                                                        <BasicMachineCameraFeed machineID={clawCustomisationData?.setup?.settings?.game[0]?.machine === 'left' ? 1 : 2} cameraYOffsetLevel={cameraYOffsetLevel} />
                                                        <div className="claw-grid-camera-gradient" />
                                                        <div className="claw-live-grid-overlay-layer" style={{ '--claw-live-grid-y-offset': `${liveGridYOffset}px` }}>
                                                        <div
                                                            className="claw-grid-camera-overlay"
                                                            style={{
                                                                backgroundColor: gridLineColour,
                                                                WebkitMaskImage: `url(${GRID_OVERLAY_IMAGES[gridSize] ?? grid20x20Image})`,
                                                                maskImage: `url(${GRID_OVERLAY_IMAGES[gridSize] ?? grid20x20Image})`,
                                                            }}
                                                        />
                                                        {/* Invisible, only used to measure the tinted overlay's actual rendered box for marker placement */}
                                                        <img
                                                            ref={gridImageRefCallback}
                                                            className="claw-grid-camera-reference-image"
                                                            src={GRID_OVERLAY_IMAGES[gridSize] ?? grid20x20Image}
                                                            alt=""
                                                            aria-hidden="true"
                                                        />
                                                        {/* Sized/positioned to the actual rendered grid image (not the whole camera feed), so hover/click (slider mode only) stay within the grid itself */}
                                                        {gridImageBox && (
                                                            <div
                                                                className={`claw-grid-hit-area${useSliderSelection ? ' clickable' : ''}`}
                                                                style={{
                                                                    left: `${gridImageBox.left}px`,
                                                                    top: `${gridImageBox.top}px`,
                                                                    width: `${gridImageBox.width}px`,
                                                                    height: `${gridImageBox.height}px`,
                                                                }}
                                                                onClick={useSliderSelection ? handleLiveGridClick : undefined}
                                                            />
                                                        )}
                                                        {liveGridMarkers.map((marker) => (
                                                            <div
                                                                key={marker.key}
                                                                className="claw-grid-camera-marker-anchor clickable"
                                                                style={{ left: `${marker.leftPercent}%`, top: `${marker.topPercent}%` }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    updateSelectedCell(marker.col, marker.row);
                                                                }}
                                                            >
                                                                <PosMarker
                                                                    x={0}
                                                                    y={0}
                                                                    tag={marker.tag}
                                                                    highlighted={!useSliderSelection && selectedCell?.col === marker.col && selectedCell?.row === marker.row}
                                                                />
                                                            </div>
                                                        ))}
                                                        {liveSelectedMarker && (
                                                            <div
                                                                key={liveSelectedMarker.key}
                                                                className="claw-grid-camera-marker-anchor"
                                                                style={{ left: `${liveSelectedMarker.leftPercent}%`, top: `${liveSelectedMarker.topPercent}%` }}
                                                            >
                                                                <PosMarker x={0} y={0} tag={liveSelectedMarker.tag} />
                                                            </div>
                                                        )}
                                                        {/*referenceCornerMarkers.map((marker) => (
                                                            <div
                                                                key={marker.key}
                                                                className="claw-grid-camera-marker-anchor"
                                                                style={{ left: `${marker.leftPercent}%`, top: `${marker.topPercent}%` }}
                                                            >
                                                                <PosMarker x={0} y={0} tag={marker.tag} />
                                                            </div>
                                                        ))*/}
                                                        </div>
                                                    </div>
                                                    <div className={`position-select-grid-overlay${optionSelectView === 'live' ? ' hidden' : ''}`}>
                                                        <ClawOptionsGrid />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="claw-controller-bottom-content-holder">
                                            {useSliderSelection ? (
                                                <p className="claw-vote-instructions">Drag sliders to choose the next claw move from the grid. ex. &ldquo;J-17&rdquo;</p>
                                            ) : (
                                                <div className="claw-poll-option-buttons">
                                                    {pollOptions.map((pollOption) => {
                                                        const isSelected = selectedCell && `#${GRID_LETTERS[selectedCell.col]}-${gridSize - selectedCell.row}` === pollOption.option;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={pollOption.option}
                                                                className={`claw-poll-option-button${isSelected ? ' active' : ''}`}
                                                                onClick={() => {
                                                                    const parsed = parsePositionString(pollOption.option, gridSize);
                                                                    if (parsed) {
                                                                        updateSelectedCell(parsed.col, parsed.row);
                                                                    }
                                                                }}
                                                            >
                                                                {pollOption.option?.substring(1) ?? pollOption.option}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                className="claw-confirm-selection-button"
                                                disabled={!selectedCell}
                                                onClick={() => {
                                                    if (selectedCell && socket.current) {
                                                        const vote = `#${GRID_LETTERS[selectedCell.col]}-${gridSize - selectedCell.row}`;
                                                        setYourSelectedPosition(vote);
                                                        socket.current.emit('clawControllerVote', { streamID: streamId, featureID: clawFeatureId, vote: vote, name: controllerName });
                                                        console.log('Sent vote to feature:', vote);
                                                    }
                                                }}
                                            >
                                                Confirm Selection{selectedCell ? `: ${GRID_LETTERS[selectedCell.col]}-${gridSize - selectedCell.row}` : ''}
                                            </button>
                                            {controllerContent.showSponsor === true && controllerContent.sponsorImage?.srcUrl && (
                                                <img className="claw-controller-sponsor-image" src={controllerContent.sponsorImage.srcUrl} alt="Sponsor" />
                                            )}
                                        </div>
                                    </>
                                }
                                {SHOW_CAM_PHASES.includes(clawPhase) &&
                                    <>
                                        <div className="claw-phase-frame" style={controllerStyles}>
                                            <div className="claw-phase-frame-box">
                                                <div className="claw-phase-frame-header">
                                                    <div className="claw-phase-tab">Selected Position: {yourSelectedPosition?.substring(1)}</div>
                                                    <div className="claw-phase-frame-actions">
                                                        <button
                                                            type="button"
                                                            className={`cam-view-toggle-button${idleCamView === 'front' ? ' active' : ''}`}
                                                            onClick={() => setIdleCamView('front')}
                                                        >
                                                            Front View
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`cam-view-toggle-button${idleCamView === 'top' ? ' active' : ''}`}
                                                            onClick={() => setIdleCamView('top')}
                                                        >
                                                            Top View
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="claw-phase-frame-body">
                                                    <BasicMachineCameraFeed machineID={clawCustomisationData?.setup?.settings?.game[0]?.machine === 'left' ? 1 : 2} cameraId={idleCamView} cameraYOffsetLevel={cameraYOffsetLevel} />
                                                    {lastResultWin === null && clawCustomisationData?.controller?.view?.videos?.[0]?.usePlayingMoveEffectVid && clawCustomisationData?.controller?.view?.videos?.[0]?.playingMoveEffectVid?.srcUrl && (
                                                        <video
                                                            key={clawCustomisationData.controller.view.videos[0].playingMoveEffectVid.srcUrl}
                                                            className="claw-playing-overlay-video"
                                                            src={clawCustomisationData.controller.view.videos[0].playingMoveEffectVid.srcUrl}
                                                            autoPlay
                                                            playsInline
                                                            loop
                                                        />
                                                    )}
                                                    {lastResultWin === true && clawCustomisationData?.controller?.view?.videos?.[0]?.winVid?.srcUrl && (
                                                        <video
                                                            key={clawCustomisationData.controller.view.videos[0].winVid.srcUrl}
                                                            className="claw-result-video"
                                                            src={clawCustomisationData.controller.view.videos[0].winVid.srcUrl}
                                                            autoPlay
                                                            playsInline
                                                        />
                                                    )}
                                                    {lastResultWin === false && clawCustomisationData?.controller?.view?.videos?.[0]?.loseVid?.srcUrl && (
                                                        <video
                                                            key={clawCustomisationData.controller.view.videos[0].loseVid.srcUrl}
                                                            className="claw-result-video"
                                                            src={clawCustomisationData.controller.view.videos[0].loseVid.srcUrl}
                                                            autoPlay
                                                            playsInline
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="claw-controller-bottom-content-holder">
                                            {letsPlayTextVid ? (
                                                <video
                                                    key={letsPlayTextVid}
                                                    className="claw-lets-play-text-video"
                                                    src={letsPlayTextVid}
                                                    autoPlay
                                                    playsInline
                                                    loop
                                                />
                                            ) : (
                                                <h1>Let's Play!</h1>
                                            )}
                                            {/*selectedPosition?.option && selectedPosition?.option !== '' && selectedPosition?.option !== yourSelectedPosition && (
                                                <p>Playing Position: {selectedPosition?.option}</p>
                                            )*/}
                                            {/*selectedPosition?.option &&
                                                <p>Playing Position: {selectedPosition?.option}</p>
                                            */}
                                            {/*yourSelectedPosition &&
                                                <p>Your Selected Position: {yourSelectedPosition}</p>
                                            */}
                                            {/*lastResultWin === true && lastResultSelectedPlayer &&
                                                <p>Selected Player: {lastResultSelectedPlayer} ({lastResultSelectedPlayerChannel})</p>
                                            */}
                                            {controllerContent.showSponsor === true && controllerContent.sponsorImage?.srcUrl && (
                                                <img className="claw-controller-sponsor-image" src={controllerContent.sponsorImage.srcUrl} alt="Sponsor" />
                                            )}
                                        </div>
                                    </>
                                }
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ClawController;

